import React, { useState } from 'react';
import { formatCurrency, formatPercentage, getPerformanceColor, safeNumber } from '../../utils/formatters';
import './PoolManagementCard.css';

const PoolManagementCard = ({ 
  pool, 
  onStatusChange, 
  onExecuteTrade, 
  onManageInvestors, 
  onViewAnalytics 
}) => {
  const [isUpdatingStatus, setIsUpdatingStatus] = useState(false);

  const handleStatusChange = async (newStatus) => {
    if (isUpdatingStatus) return;
    
    setIsUpdatingStatus(true);
    try {
      await onStatusChange(pool.id, newStatus);
    } finally {
      setIsUpdatingStatus(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'green';
      case 'paused': return 'orange';
      case 'closed': return 'red';
      default: return 'gray';
    }
  };

  const formatCurrencyLocal = (amount) => {
    return new Intl.NumberFormat('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 6
    }).format(safeNumber(amount));
  };

  return (
    <div className="pool-management-card">
      <div className="pool-header">
        <div className="pool-title">
          <h3>{pool.name}</h3>
          <div className={`pool-status ${pool.status}`}>
            <span className={`status-indicator ${getStatusColor(pool.status)}`}></span>
            {pool.status.toUpperCase()}
          </div>
        </div>
        
        <div className="pool-actions-menu">
          <select 
            value={pool.status} 
            onChange={(e) => handleStatusChange(e.target.value)}
            disabled={isUpdatingStatus}
            className="status-selector"
          >
            <option value="active">Active</option>
            <option value="paused">Paused</option>
            <option value="closed">Closed</option>
          </select>
        </div>
      </div>

      <div className="pool-description">
        <p>{pool.description}</p>
      </div>

      <div className="pool-metrics">
        <div className="metrics-row">
          <div className="metric">
            <span className="metric-label">Total Size</span>
            <span className="metric-value">{formatCurrencyLocal(pool.totalSize)} SOL</span>
          </div>
          <div className="metric">
            <span className="metric-label">Investors</span>
            <span className="metric-value">{pool.investorCount || 0}</span>
          </div>
        </div>

        <div className="metrics-row">
          <div className="metric">
            <span className="metric-label">30d Performance</span>
            <span className={`metric-value ${getPerformanceColor(pool.performance30d)}`}>
              {formatPercentage(pool.performance30d)}
            </span>
          </div>
          <div className="metric">
            <span className="metric-label">Total Return</span>
            <span className={`metric-value ${getPerformanceColor(pool.totalReturn)}`}>
              {formatPercentage(pool.totalReturn)}
            </span>
          </div>
        </div>

        <div className="metrics-row">
          <div className="metric">
            <span className="metric-label">Management Fee</span>
            <span className="metric-value">{safeNumber(pool.managementFee).toFixed(2)}%</span>
          </div>
          <div className="metric">
            <span className="metric-label">Performance Fee</span>
            <span className="metric-value">{safeNumber(pool.performanceFee).toFixed(2)}%</span>
          </div>
        </div>

        <div className="metrics-row">
          <div className="metric">
            <span className="metric-label">Fees Collected</span>
            <span className="metric-value">{formatCurrencyLocal(pool.feesCollected || 0)} SOL</span>
          </div>
          <div className="metric">
            <span className="metric-label">Risk Level</span>
            <span className={`metric-value risk-${pool.riskLevel}`}>
              {pool.riskLevel?.toUpperCase() || 'MEDIUM'}
            </span>
          </div>
        </div>
      </div>

      <div className="pool-strategy">
        <h4>Strategy</h4>
        <p>{pool.strategy}</p>
      </div>

      <div className="recent-activity">
        <h4>Recent Activity</h4>
        {pool.recentTrades && pool.recentTrades.length > 0 ? (
          <div className="activity-list">
            {pool.recentTrades.slice(0, 3).map((trade, index) => (
              <div key={index} className="activity-item">
                <span className={`trade-type ${trade.type}`}>
                  {trade.type.toUpperCase()}
                </span>
                <span className="trade-token">{trade.tokenSymbol}</span>
                <span className="trade-amount">{formatCurrency(trade.amount)} SOL</span>
                <span className="trade-time">{new Date(trade.executedAt).toLocaleDateString()}</span>
              </div>
            ))}
          </div>
        ) : (
          <p className="no-activity">No recent trading activity</p>
        )}
      </div>

      <div className="pending-withdrawals">
        {pool.pendingWithdrawals > 0 && (
          <div className="withdrawal-alert">
            <span className="alert-icon">⚠️</span>
            <span>{pool.pendingWithdrawals} pending withdrawal{pool.pendingWithdrawals > 1 ? 's' : ''}</span>
          </div>
        )}
      </div>

      <div className="pool-management-actions">
        <button 
          className="action-btn primary"
          onClick={onExecuteTrade}
          disabled={pool.status !== 'active'}
        >
          Execute Trade
        </button>
        
        <button 
          className="action-btn secondary"
          onClick={onManageInvestors}
        >
          Manage Investors
        </button>
        
        <button 
          className="action-btn secondary"
          onClick={onViewAnalytics}
        >
          View Analytics
        </button>
      </div>

      <div className="pool-footer">
        <div className="pool-dates">
          <span className="created-date">
            Created: {new Date(pool.createdAt).toLocaleDateString()}
          </span>
          {pool.lastTradeAt && (
            <span className="last-trade">
              Last Trade: {new Date(pool.lastTradeAt).toLocaleDateString()}
            </span>
          )}
        </div>
      </div>
    </div>
  );
};

export default PoolManagementCard;